import Link from 'next/link';
import { motion } from 'motion/react';
import { ArrowLeft, Mail, ArrowRight } from 'lucide-react';
import { CAREERS_COPY } from '../../../constants/careersTranslations';
import { CONTACT_PEOPLE } from '../contact/contactData';
import { CAREERS_FI, CAREERS_EN } from '../../../lib/blogPaths';
import { Container } from '../../Container';

const RECRUITMENT_EMAIL = 'rekry@identio.fi';

/** Contact people for recruitment (Helsinki + Turku). */
const RECRUITMENT_CONTACTS = CONTACT_PEOPLE.filter(
  (p) => p.role && (p.role.includes('Recruitment') || p.role.includes('Rekry')),
);

/** Read ACF field supporting both snake_case and camelCase (WPGraphQL for ACF can expose either). */
function getAcf(rp, snakeKey, camelKey) {
  if (!rp) return undefined;
  const k = camelKey ?? snakeKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return rp[snakeKey] ?? rp[k];
}

/**
 * Single job post page (rekry/careers detail). Built only from ACF "Recruitment Position" data.
 * Elementor and post content are never used – only ACF fields. Visuals match careers sections.
 */
export function JobPostPage({ post, recruitmentPosition = {}, lang = 'fi', basePath = '/rekry' }) {
  const rp = recruitmentPosition;
  const t = CAREERS_COPY[lang]?.jobPost ?? CAREERS_COPY.en.jobPost;
  const benefitsCopy = CAREERS_COPY[lang]?.benefits ?? CAREERS_COPY.en.benefits;
  const title = getAcf(rp, 'position_title') ?? post?.title ?? '';
  const location = getAcf(rp, 'location') ?? '';
  const authorName = post?.author?.node?.name ?? '';
  const dateStr = post?.date
    ? new Date(post.date).toLocaleDateString(lang === 'fi' ? 'fi-FI' : 'en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
  const otherLangUrl =
    getAcf(rp, 'in_english_suomeksi_link') ??
    (post?.slug ? (lang === 'fi' ? `${CAREERS_EN}/${post.slug}` : `${CAREERS_FI}/${post.slug}`) : null);

  const expectations = [
    getAcf(rp, 'expectation_1'),
    getAcf(rp, 'expectation_2'),
    getAcf(rp, 'expectation_3'),
    getAcf(rp, 'expectation_4'),
    getAcf(rp, 'expectation_5'),
    getAcf(rp, 'expectation_6'),
    getAcf(rp, 'expectation_7'),
    getAcf(rp, 'expectation_8'),
    getAcf(rp, 'expectation_9'),
  ].filter(Boolean);

  const hasAcfContent =
    getAcf(rp, 'title_1') ||
    getAcf(rp, 'description_1') ||
    getAcf(rp, 'tasks_title') ||
    getAcf(rp, 'tasks_description') ||
    getAcf(rp, 'salary_title') ||
    getAcf(rp, 'salary_description') ||
    expectations.length > 0;

  return (
    <article className="min-h-screen bg-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden>
        <div className="absolute top-0 right-0 w-96 h-96 bg-highlight rounded-full blur-3xl" />
      </div>

      <div className="section-rhythm relative z-10">
        <Container>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
            >
              <Link
                href={basePath}
                className="inline-flex items-center gap-2 text-gray-400 hover:text-highlight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f172a] rounded"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" />
                {t.back}
              </Link>
              {otherLangUrl && (
                <p className="text-sm text-gray-400 mt-3">
                  {lang === 'fi' ? (
                    <Link href={otherLangUrl} className="hover:text-highlight underline">
                      In English
                    </Link>
                  ) : (
                    <Link href={otherLangUrl} className="hover:text-highlight underline">
                      Suomeksi
                    </Link>
                  )}
                </p>
              )}
            </motion.div>

            <motion.header
              className="mb-12"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              <p className="text-sm text-gray-400 mb-1">
                {dateStr}
                {authorName ? ` ${lang === 'fi' ? '–' : 'by'} ${authorName}` : ''}
              </p>
              {location ? (
                <p className="text-highlight text-sm font-medium mb-2">{location}</p>
              ) : null}
              <h1 className="hero-headline text-white mb-0">{title}</h1>
            </motion.header>

            {(getAcf(rp, 'title_1') || getAcf(rp, 'description_1')) && (
              <motion.section
                className="mb-12"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                {getAcf(rp, 'title_1') && (
                  <h2 className="heading-2 text-white mb-4">{getAcf(rp, 'title_1')}</h2>
                )}
                {getAcf(rp, 'description_1') && (
                  <div
                    className="prose prose-invert prose-lg max-w-none text-gray-300 mb-6 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: getAcf(rp, 'description_1') }}
                  />
                )}
                {getAcf(rp, 'link_1') && getAcf(rp, 'link_1_url') && (
                  <Link
                    href={getAcf(rp, 'link_1_url')}
                    className="inline-flex items-center gap-2 text-highlight font-semibold hover:gap-3 transition-all"
                  >
                    {getAcf(rp, 'link_1')}
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </Link>
                )}
              </motion.section>
            )}

            {(getAcf(rp, 'tasks_title') || getAcf(rp, 'tasks_description')) && (
              <motion.section
                className="mb-12"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
              >
                {getAcf(rp, 'tasks_title') && (
                  <h2 className="heading-3 text-white mb-3">{getAcf(rp, 'tasks_title')}</h2>
                )}
                {getAcf(rp, 'tasks_description') && (
                  <div
                    className="prose prose-invert max-w-none text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: getAcf(rp, 'tasks_description') }}
                  />
                )}
              </motion.section>
            )}

            {(getAcf(rp, 'salary_title') || getAcf(rp, 'salary_description')) && (
              <motion.section
                className="mb-12"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                {getAcf(rp, 'salary_title') && (
                  <h2 className="heading-3 text-white mb-3">{getAcf(rp, 'salary_title')}</h2>
                )}
                {getAcf(rp, 'salary_description') && (
                  <div
                    className="prose prose-invert max-w-none text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: getAcf(rp, 'salary_description') }}
                  />
                )}
              </motion.section>
            )}

            {expectations.length > 0 && (
              <motion.section
                className="mb-12"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 }}
              >
                {getAcf(rp, 'expectations_title') && (
                  <h2 className="heading-3 text-white mb-4">{getAcf(rp, 'expectations_title')}</h2>
                )}
                <ul className="space-y-2 text-gray-300 text-lg leading-relaxed">
                  {expectations.map((item, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-highlight shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.section>
            )}

            {!hasAcfContent && (
              <motion.p
                className="text-gray-400 mb-12 text-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {lang === 'fi'
                  ? 'Työpaikkailmoituksen sisältöä ei ole vielä täytetty ACF-kentissä.'
                  : 'Job listing content has not been filled in the ACF fields yet.'}
              </motion.p>
            )}

            {benefitsCopy?.linkHref && (
              <motion.p
                className="mb-12"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <Link
                  href={benefitsCopy.linkHref}
                  className="inline-flex items-center gap-2 text-highlight font-semibold hover:gap-3 transition-all"
                >
                  {t.benefitsLink}
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </Link>
              </motion.p>
            )}

            <motion.section
              className="border-t border-white/10 pt-10"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
            >
              <h2 className="heading-3 text-white mb-2">{t.applyTitle}</h2>
              <p className="text-gray-300 mb-6 text-lg">
                {t.applyEmail}{' '}
                <a
                  href={`mailto:${RECRUITMENT_EMAIL}`}
                  className="text-highlight font-semibold hover:underline"
                >
                  {RECRUITMENT_EMAIL}
                </a>
              </p>
              {RECRUITMENT_CONTACTS.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {RECRUITMENT_CONTACTS.map((person) => (
                    <div
                      key={person.email}
                      className="p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                    >
                      <p className="font-semibold text-white mb-0.5">{person.name}</p>
                      <p className="text-sm text-gray-400 mb-3">{person.title}</p>
                      <a
                        href={`mailto:${person.email}`}
                        className="text-highlight text-sm font-medium hover:underline inline-flex items-center gap-1.5"
                      >
                        <Mail className="w-4 h-4 shrink-0" />
                        {person.email}
                      </a>
                      {person.phone && (
                        <a
                          href={`tel:${person.phone.replace(/\s/g, '')}`}
                          className="text-gray-400 text-sm hover:text-highlight block mt-1"
                        >
                          {person.phone}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.section>
          </div>
        </Container>
      </div>
    </article>
  );
}
